import { DndContext, DragEndEvent, DragMoveEvent, DragOverlay, DragStartEvent, PointerSensor, useSensor, useSensors, closestCenter, pointerWithin, Modifier, CollisionDetection } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { orderBy } from 'lodash/fp';
import { MouseEventHandler, memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useOnValueChange } from '../../../../hooks/useOnValueChange';
import { useDispatch } from 'react-redux';
import { SelectItemContent } from '../../../../components/selects/SelectItem';
import { RendererReadOnlyContext } from '../../../../contexts/RendererReadOnlyContext';
import { isAnnotationContextGroup } from '../../../../contexts/AnnotationContext';
import { getAnnotationNameProperty } from '@pointorama/potree-shared';
import { useGroupOperations } from '../../../../hooks/potree/useGroupOperations';
import { useAnnotations } from '../../../../hooks/potree/useRenderer';
import { useOpeningsByRoomId } from '../../../../hooks/potree/useOpeningsByRoomId';
import { useShouldHideAnnotationType, useShouldHideGroupType } from '../../../../hooks/potree/useShouldHideAnnotationType';
import { useSelectAnnotation } from '../../../../hooks/potree/useSelectAnnotation';
import { annotationStore, useAnnotationIds, useAnnotationById, getAnnotationsByIdentifier } from '../../../../hooks/potree/useAnnotationStore';
import { useToastNotifications, ToastType } from '../../../../hooks/useToastNotifications';
import { unSelectAll } from '../../../../redux/rendererReducer';
import store from '../../../../redux/store';
import { GroupType, type Project } from '../../../../types/graphqlTypes';
import { Annotation, type AnnotationProps } from './Annotation';
import { AnnotationGroup } from './AnnotationGroup';
import {
  type AnnotationsWithIndices,
  type DropTarget,
  type DestData,
  type DragSourceData,
  type PointerZone,
  computeSourcePosition,
  resolvePointerZone,
  validateMove,
  calculateDropPosition,
  computeInsertionIndex,
  toFullContextIndex,
} from './utils/resolveDropPosition';
import { GROUP_ICONS_BY_TYPE, useAnnotationIcon, type AnnotationTypeName } from '../../../../utils/annotationIcons';
import { DropIndicatorLine } from './DropIndicatorLine';
import { ContextMenu } from '../../../../components/ContextMenu';
import { T, useT } from '../../../../translation/src';
import { FolderIcon } from '@heroicons/react/24/outline';
import useOpen from '../../../../hooks/useOpen';
import { AnnotationGroupModal } from '../../../project/AnnotationGroupModal';

export type DropIndicator = { index: number; groupId?: string } | null;

const DragOverlayContent: React.FC<{
  item: AnnotationsWithIndices[0];
  isMultiDrag: boolean;
  orderedSelectedAnnotations: { type: string; identifier: string; groupIdentifier?: string }[];
}> = ({ item, isMultiDrag, orderedSelectedAnnotations }) => {
  const isGroup = isAnnotationContextGroup(item);
  // Dev's reactive icon hook (sketch-object per-type icons + subtype
  // subscriptions) combined with staging's unknown-group-type fallback.
  const annotationIcon = useAnnotationIcon(item as { type: string; identifier: string });
  const Icon = isGroup ? (GROUP_ICONS_BY_TYPE[item.type as GroupType] ?? FolderIcon) : annotationIcon;

  // Filter out children whose parent group is also selected
  const selectedIds = new Set(orderedSelectedAnnotations.map((a) => a.identifier));
  const additionalCount = isMultiDrag ? orderedSelectedAnnotations.filter((a) => !a.groupIdentifier || !selectedIds.has(a.groupIdentifier)).length - 1 : 0;

  return (
    <SelectItemContent icon={Icon} active className="bg-neon-green-300/70! dark:bg-black/70! dark:text-white shadow-lg min-w-48">
      <div className="flex-1 overflow-hidden">{isAnnotationContextGroup(item) ? item.name : getAnnotationNameProperty(item.identifier).value}</div>
      {additionalCount > 0 && <div className="ml-auto px-1.5 py-0.5 text-xs font-medium bg-neon-green-500 text-black rounded">+{additionalCount}</div>}
    </SelectItemContent>
  );
};

/** Wrapper that reads selection from Redux — avoids ref access in parent render */
const DragOverlayContentWrapper: React.FC<{
  item: AnnotationsWithIndices[0];
  isMultiDrag: (id?: string) => boolean;
  annotationsByIdentifier: Record<string, unknown>;
}> = ({ item, isMultiDrag, annotationsByIdentifier }) => {
  const selectedAnnotations = store.getState().rendererProvider.selectedAnnotations;
  const orderedSelectedAnnotations = orderBy(
    'index',
    'asc',
    selectedAnnotations.map((s) => ({ ...s, index: (annotationsByIdentifier[s.identifier] as { index?: number } | undefined)?.index })),
  );
  return <DragOverlayContent item={item} isMultiDrag={isMultiDrag()} orderedSelectedAnnotations={orderedSelectedAnnotations} />;
};

const POINTER_SENSOR_OPTIONS = { activationConstraint: { distance: 8 } };

/** Memoized list body — only re-renders when annotation IDs change, not on every data update.
 *  Individual items read their own data from hooks/context. */
/** Single list item — subscribes to its own annotation data from the store.
 *  When other annotations change, this component is NOT visited by React
 *  because it has no parent re-render trigger — only the store subscription. */
const AnnotationItemById = memo(
  ({
    id,
    index,
    isFirst,
    isLast,
    isOpen,
    project,
    isReadOnly,
    onClickAnnotation,
    activeId,
    dropIndicator,
    dragOverGroupId,
    onGroupOpenChange,
    pendingHideIds,
    openingsByRoomId,
    openRooms,
    onRoomOpenChange,
    openGroups,
  }: {
    id: string;
    index: number;
    isFirst: boolean;
    isLast: boolean;
    isOpen: boolean;
    project: Pick<Project, 'id'>;
    isReadOnly: boolean;
    onClickAnnotation: AnnotationProps['onClickAnnotation'];
    activeId: string | null;
    dropIndicator: DropIndicator;
    dragOverGroupId: string | null;
    onGroupOpenChange: (groupId: string, isOpen: boolean) => void;
    pendingHideIds: Set<string> | null;
    openingsByRoomId: Record<string, string[]>;
    openRooms: Set<string>;
    onRoomOpenChange: (roomId: string, isOpen: boolean) => void;
    openGroups: Set<string>;
  }) => {
    const annotation = useAnnotationById(id);
    if (!annotation) return null;

    // Item is hidden while waiting for the drop mutation response
    if (pendingHideIds?.has(id)) return null;

    if (!isAnnotationContextGroup(annotation)) {
      return (
        <>
          {activeId && dropIndicator && !dropIndicator.groupId && dropIndicator.index === index && <DropIndicatorLine />}
          <Annotation
            project={project}
            isReadOnly={isReadOnly}
            type={annotation.type as AnnotationTypeName}
            annotation={annotation}
            isFirst={isFirst}
            isLast={isLast}
            onClickAnnotation={onClickAnnotation}
            rootIndex={index}
            linkedOpeningIds={openingsByRoomId[id]}
            isRoomOpen={openRooms.has(id)}
            onRoomOpenChange={onRoomOpenChange}
          />
        </>
      );
    }
    return (
      <>
        {activeId && dropIndicator && !dropIndicator.groupId && dropIndicator.index === index && <DropIndicatorLine />}
        <AnnotationGroup
          project={project}
          isReadOnly={isReadOnly}
          group={annotation}
          isFirst={isFirst}
          isLast={isLast}
          onClickAnnotation={onClickAnnotation}
          rootIndex={index}
          dropIndicator={activeId ? dropIndicator : null}
          isDragOver={dragOverGroupId === id}
          isOpen={isOpen}
          onGroupOpenChange={onGroupOpenChange}
          openingsByRoomId={openingsByRoomId}
          openRooms={openRooms}
          onRoomOpenChange={onRoomOpenChange}
          openGroups={openGroups}
        />
      </>
    );
  },
);

/** List body — renders items by ID only. Each item subscribes to its own data
 *  from the annotation store via useSyncExternalStore. When an annotation is
 *  created, React only mounts the new item — existing items are not visited. */
const AnnotationListItems = memo(
  ({
    ids,
    project,
    isReadOnly,
    onClickAnnotation,
    activeId,
    dropIndicator,
    dragOverGroupId,
    openGroups,
    onGroupOpenChange,
    pendingHideIds,
    openingsByRoomId,
    openRooms,
    onRoomOpenChange,
  }: {
    ids: string[];
    project: Pick<Project, 'id'>;
    isReadOnly: boolean;
    onClickAnnotation: AnnotationProps['onClickAnnotation'];
    activeId: string | null;
    dropIndicator: DropIndicator;
    dragOverGroupId: string | null;
    openGroups: Set<string>;
    onGroupOpenChange: (groupId: string, isOpen: boolean) => void;
    pendingHideIds: Set<string> | null;
    openingsByRoomId: Record<string, string[]>;
    openRooms: Set<string>;
    onRoomOpenChange: (roomId: string, isOpen: boolean) => void;
  }) => {
    return (
      <>
        {ids.map((id, index) => (
          <AnnotationItemById
            key={id}
            id={id}
            index={index}
            isFirst={index === 0}
            isLast={index === ids.length - 1}
            project={project}
            isReadOnly={isReadOnly}
            onClickAnnotation={onClickAnnotation}
            activeId={activeId}
            dropIndicator={dropIndicator}
            dragOverGroupId={dragOverGroupId}
            isOpen={openGroups.has(id)}
            onGroupOpenChange={onGroupOpenChange}
            pendingHideIds={pendingHideIds}
            openingsByRoomId={openingsByRoomId}
            openRooms={openRooms}
            onRoomOpenChange={onRoomOpenChange}
            openGroups={openGroups}
          />
        ))}
        {activeId && dropIndicator && !dropIndicator.groupId && dropIndicator.index >= ids.length && <DropIndicatorLine />}
      </>
    );
  },
);

const Annotations_: React.FC2<{ project: Pick<Project, 'id'>; defaultSelectedAnnotationId?: string }> = ({ project, defaultSelectedAnnotationId }) => {
  const draggingText = useT('dragging...', { swc: true });
  const sensors = useSensors(useSensor(PointerSensor, POINTER_SENSOR_OPTIONS));
  const { onClose: closeCreateGroupModal, onOpen: openCreateGroupModal, open: createGroupModalOpen } = useOpen();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [dropIndicator, setDropIndicator] = useState<DropIndicator>(null);
  const [dragOverGroupId, setDragOverGroupId] = useState<string | null>(null);
  const dropTargetRef = useRef<DropTarget | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const lastPointerZoneRef = useRef<PointerZone | undefined>(undefined);
  const dragListenersCleanupRef = useRef<(() => void) | null>(null);
  const dragSourcePositionRef = useRef<{ index: number; groupId?: string } | null>(null);
  const currentPointerYRef = useRef<number | null>(null);

  // Track pointer position globally for drop calculations
  useEffect(() => {
    const trackPointer = (e: PointerEvent) => {
      currentPointerYRef.current = e.clientY;
    };
    // Also capture position on pointerdown to ensure we have it before drag starts
    const trackPointerDown = (e: PointerEvent) => {
      currentPointerYRef.current = e.clientY;
    };
    document.addEventListener('pointermove', trackPointer);
    document.addEventListener('pointerdown', trackPointerDown);
    return () => {
      document.removeEventListener('pointermove', trackPointer);
      document.removeEventListener('pointerdown', trackPointerDown);
    };
  }, []);

  const [{ indexedAnnotations }] = useAnnotations();

  // Sync the external store when annotation data changes.
  // The store only notifies subscribers if data actually changed.
  useEffect(() => {
    annotationStore.update(indexedAnnotations);
  }, [indexedAnnotations]);

  // Subscribe to the ordered ID list from the store. This is a stable string[]
  // reference that only changes when IDs are added/removed/reordered.
  const annotationIds = useAnnotationIds();

  // Openings are nested under their linked rooms — not part of the flat tree.
  const { allOpeningIds, byRoomId: openingsByRoomId } = useOpeningsByRoomId();

  // Persona-aware visibility: the Layout persona shows only floors + sketch
  // objects (everything else hidden via allowlist); other personas hide the
  // layout-only sketch objects. Floors are groups and always show. Mirrors the
  // viewport blacklist so panel + scene agree.
  const shouldHideType = useShouldHideAnnotationType();
  const shouldHideGroupType = useShouldHideGroupType();

  // type lookup for every annotation (top-level + group children) so we can
  // filter both the flat tree and floor-group children by the active persona.
  const typeById = useMemo(() => {
    const map = new Map<string, string>();
    indexedAnnotations.forEach((a) => {
      if (isAnnotationContextGroup(a)) {
        a.annotations.forEach((child) => map.set(child.identifier, child.type));
      } else {
        map.set(a.identifier, a.type);
      }
    });
    return map;
  }, [indexedAnnotations]);

  // Group-type lookup for top-level group rows. In the Layout persona only FLOOR
  // groups stay; SURFACE and normal (DEFAULT) groups are hidden.
  const groupTypeById = useMemo(() => {
    const map = new Map<string, GroupType>();
    indexedAnnotations.forEach((a) => {
      if (isAnnotationContextGroup(a)) map.set(a.identifier, a.type);
    });
    return map;
  }, [indexedAnnotations]);

  const isTypeHidden = useCallback(
    (id: string) => {
      const groupType = groupTypeById.get(id);
      if (groupType !== undefined) {
        // A group row: persona-filtered (room folders only in the floorplan
        // persona; Layout keeps only FLOOR groups).
        return shouldHideGroupType(groupType);
      }
      const type = typeById.get(id);
      return type !== undefined && shouldHideType(type);
    },
    [shouldHideType, shouldHideGroupType, typeById, groupTypeById],
  );

  const visibleAnnotationIds = useMemo(
    () => annotationIds.filter((id) => !allOpeningIds.has(id) && !isTypeHidden(id)),
    [annotationIds, allOpeningIds, isTypeHidden],
  );

  // Drag & drop operates on the VISIBLE tree only: rows hidden by the active
  // tab's type filters render nowhere, so they must not occupy drop indices
  // either — a full-tree index displayed against the filtered rows makes the
  // indicator (and the drop) land somewhere other than the hover. Mirrors the
  // row-render filters (AnnotationGroup.visibleChildren / visibleAnnotationIds).
  const dndAnnotations = useMemo(() => {
    const filterItems = (items: typeof indexedAnnotations): typeof indexedAnnotations =>
      items
        .filter((item) => (isAnnotationContextGroup(item) ? !shouldHideGroupType(item.type) : item.type !== 'OPENING' && !shouldHideType(item.type)))
        .map((item) => (isAnnotationContextGroup(item) ? ({ ...item, annotations: filterItems(item.annotations as typeof indexedAnnotations) } as (typeof indexedAnnotations)[number]) : item));
    return filterItems(indexedAnnotations);
  }, [indexedAnnotations, shouldHideType, shouldHideGroupType]);

  // Keep a ref with the latest sortable items, but only pass them to
  // SortableContext when a drag is active. This prevents SortableContext
  // from updating its internal context on every annotation create/delete,
  // which would cause React to walk all 1200+ child fibers.
  // Openings are excluded so they cannot be drag sources or drop targets.
  const latestSortableItems = useMemo(() => {
    const items: string[] = [];
    const visit = (item: (typeof indexedAnnotations)[number]): void => {
      if (allOpeningIds.has(item.identifier)) return;
      items.push(item.identifier);
      // Depth-first: nested room folders and their children sort too.
      if (isAnnotationContextGroup(item)) {
        item.annotations.forEach((child) => visit(child as (typeof indexedAnnotations)[number]));
      }
    };
    // Visible tree only — hidden rows must not participate in sorting.
    dndAnnotations.forEach(visit);
    return items;
  }, [dndAnnotations, allOpeningIds]);
  const latestSortableItemsRef = useRef(latestSortableItems);
  useEffect(() => {
    latestSortableItemsRef.current = latestSortableItems;
  }, [latestSortableItems]);
  // Frozen items: only updates when drag starts/ends, not on every list change
  const [sortableItems, setSortableItems] = useState<string[]>([]);
  useEffect(() => {
    if (activeId) {
      // Drag started — sync sortable items to current list
      setSortableItems(latestSortableItemsRef.current);
    }
  }, [activeId]);
  const closedGroupItems = useRef<HTMLElement[]>([]);
  const hiddenAnnotationItems = useRef<HTMLElement[]>([]);
  const isReadOnly = useContext(RendererReadOnlyContext);
  const { showToast } = useToastNotifications();
  const actionNotSupportedTitle = useT('Action not supported');
  const roomsStayInFloorText = useT('Rooms cannot be moved outside of their floor');
  const wallSketchesStayInFloorText = useT('Wall sketches cannot be moved outside of their floor');
  const roomFoldersStayInFloorText = useT('Room folders can only be reordered within their floor');
  const sketchesStayInFloorText = useT('Sketches cannot be moved to another floor');
  const onlySketchesInRoomFolderText = useT('Only sketch objects and openings can be placed in a room folder');
  const selectedDefaultAnnotation = useRef(false);
  const [closedGroups, setClosedGroups] = useState<Set<string>>(new Set());
  const openGroups = useMemo(() => {
    const open = new Set<string>();
    const visit = (item: (typeof indexedAnnotations)[number]) => {
      if (!isAnnotationContextGroup(item)) return;
      if (!closedGroups.has(item.identifier)) open.add(item.identifier);
      // Nested groups (a floor's room folders) track open state in the same set.
      item.annotations.forEach((child) => visit(child as (typeof indexedAnnotations)[number]));
    };
    indexedAnnotations.forEach(visit);
    return open;
  }, [indexedAnnotations, closedGroups]);
  // Rooms are collapsed by default; this set tracks rooms the user has opened.
  const [openRooms, setOpenRooms] = useState<Set<string>>(new Set());
  // Selection state via ref — Annotations_ does NOT re-render on selection changes.
  // Individual Annotation/AnnotationGroup components read their own active state from Redux.
  // This ref is only used by drag callbacks (which run on user interaction, not render).
  const selectedAnnotationsRef = useRef(store.getState().rendererProvider.selectedAnnotations);
  useEffect(() => {
    // Subscribe to Redux outside of render to update the ref without causing re-renders
    const unsubscribe = store.subscribe(() => {
      selectedAnnotationsRef.current = store.getState().rendererProvider.selectedAnnotations;
    });
    return unsubscribe;
  }, []);
  // Annotation data for drag callbacks — reads from the store (no re-renders).
  const annotationsByIdentifierRef = useRef(getAnnotationsByIdentifier());
  useEffect(() => {
    annotationsByIdentifierRef.current = getAnnotationsByIdentifier();
  }, [indexedAnnotations]);

  // These helpers read selectedAnnotations from the ref — only call from event handlers, not render.
  const getOrderedSelectedAnnotations = useCallback(() => {
    const sa = selectedAnnotationsRef.current;
    const abid = annotationsByIdentifierRef.current;
    const withIndices = sa.map((sel: { identifier: string; type: string; groupIdentifier?: string }) => ({
      ...sel,
      index: abid[sel.identifier]?.index,
    }));
    return orderBy('index', 'asc', withIndices);
  }, []);
  const isMultiDrag = useCallback(
    (id?: string) => {
      const checkId = id ?? activeId;
      const sa = selectedAnnotationsRef.current;
      return sa.length > 1 && sa.some((a: { identifier: string }) => a.identifier === checkId);
    },
    [activeId],
  );

  // Modifier for multi-drag only: continuously snap the card center to the cursor.
  // Single drag uses dnd-kit's default behavior (card follows mouse with original grab offset).
  const snapYCenterToCursor: Modifier = useCallback(
    ({ transform, draggingNodeRect }) => {
      // Only apply to multi-drag
      if (!isMultiDrag() || !draggingNodeRect) {
        return transform;
      }

      // Get current pointer position
      const pointerY = currentPointerYRef.current;
      if (pointerY === null) {
        return transform;
      }

      // Calculate where the card center currently is
      const currentCenterY = draggingNodeRect.top + transform.y + draggingNodeRect.height / 2;
      // Snap center to cursor
      const correction = pointerY - currentCenterY;

      return {
        ...transform,
        y: transform.y + correction,
      };
    },
    [isMultiDrag],
  );
  const { updateOrdering } = useGroupOperations();
  const dispatch = useDispatch();

  // Helper to execute position update action
  const executePositionUpdate = useCallback(
    (action: { toIndex: number; identifiers: { type: string; identifier: string }[]; toGroupIdentifier?: string }) => {
      updateOrdering({
        identifiers: action.identifiers,
        toIndex: action.toIndex,
        toGroupIdentifier: action.toGroupIdentifier,
      });
    },
    [updateOrdering],
  );

  // Stores the resolved drop target in a ref and sets the visual indicator.
  // calculateDropPosition returns visual positions directly (where the indicator should appear),
  // so no index adjustment is needed here.
  const setDropTarget = useCallback((target: DropTarget | null) => {
    dropTargetRef.current = target;
    if (!target || !('toIndex' in target)) {
      setDropIndicator(null);
      setDragOverGroupId(null);
      return;
    }

    setDropIndicator({ index: target.toIndex, groupId: target.toGroupIdentifier });
    setDragOverGroupId(target.toGroupIdentifier || null);
  }, []);

  const onClickContainer: MouseEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      if (event.target !== event.currentTarget) return;
      dispatch(unSelectAll({}));
    },
    [dispatch],
  );

  const { onClickAnnotation } = useSelectAnnotation({ openingsByRoomId, openRooms });

  useEffect(() => {
    if (defaultSelectedAnnotationId) {
      const annotation = annotationsByIdentifierRef.current[defaultSelectedAnnotationId];
      // Only select the annotation the first time on Render. Otherwise, it will reselect the identifier over and over again when creating new annotations, ...
      if (!annotation || selectedDefaultAnnotation.current) return;
      if (isAnnotationContextGroup(annotation)) onClickAnnotation({ identifier: annotation.identifier, type: 'group' }, {});
      else
        onClickAnnotation(
          {
            identifier: annotation.identifier,
            type: 'annotation',
            groupIdentifier: annotation.groupIdentifier || undefined,
          },
          {},
        );
      selectedDefaultAnnotation.current = true;
    }
  }, [defaultSelectedAnnotationId, onClickAnnotation]);

  const onCloseGroup = useCallback(({ groupId }: { groupId: string }) => {
    const el = document.querySelector(`[data-group-children="${groupId}"]`) as HTMLElement | null;
    if (el) {
      el.classList.add('hidden');
      closedGroupItems.current = closedGroupItems.current.concat(el);
    }
  }, []);
  const onHideGroup = useCallback(({ groupId }: { groupId: string }) => {
    const elements = document.querySelectorAll<HTMLElement>(`[data-group-id="${groupId}"]`);
    elements.forEach((el) => el.classList.add('hidden'));
    hiddenAnnotationItems.current = hiddenAnnotationItems.current.concat(...Array.from(elements));
  }, []);
  const onHideAnnotation = useCallback(({ annotationId }: { annotationId: string }) => {
    const elements = document.querySelectorAll<HTMLElement>(`[data-annotation-id="${annotationId}"]`);
    elements.forEach((el) => el.classList.add('hidden'));
    hiddenAnnotationItems.current = hiddenAnnotationItems.current.concat(...Array.from(elements));
  }, []);

  const onGroupOpenChange = useCallback((groupId: string, isOpen: boolean) => {
    setClosedGroups((prev) => {
      const next = new Set(prev);
      if (isOpen) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  }, []);

  // Rooms default to closed; the set holds room IDs the user has explicitly opened.
  const onRoomOpenChange = useCallback((roomId: string, isOpen: boolean) => {
    setOpenRooms((prev) => {
      const next = new Set(prev);
      if (isOpen) {
        next.add(roomId);
      } else {
        next.delete(roomId);
      }
      return next;
    });
  }, []);

  const onDragStart = useCallback(
    (event: DragStartEvent) => {
      const sourceData = event.active.data.current as DragSourceData;
      const identifier = sourceData?.identifier;
      setActiveId(identifier);

      // Capture initial pointer position for edge zone detection
      const activatorEvent = event.activatorEvent as PointerEvent | MouseEvent | undefined;
      if (activatorEvent && 'clientY' in activatorEvent) {
        currentPointerYRef.current = activatorEvent.clientY;
      }

      lastPointerZoneRef.current = undefined;

      // Scroll wheel during drag: the fixed DragOverlay intercepts wheel events,
      // so redirect them to the actual scroll container.
      const container = scrollContainerRef.current;
      const onWheel = (e: WheelEvent) => {
        if (container) {
          e.preventDefault();
          container.scrollBy(0, e.deltaY);
        }
      };
      document.addEventListener('wheel', onWheel, { passive: false });

      // Store cleanup so onDragEnd can remove it
      dragListenersCleanupRef.current = () => {
        document.removeEventListener('wheel', onWheel);
      };

      dragSourcePositionRef.current = computeSourcePosition(sourceData, indexedAnnotations);

      // Handle multi-select drag: hide non-active selected items.
      if (isMultiDrag(identifier)) {
        getOrderedSelectedAnnotations().forEach((annotation) => {
          if (annotation.identifier === identifier) return;
          if (annotation.type === 'group') {
            onCloseGroup({ groupId: annotation.identifier });
            onHideGroup({ groupId: annotation.identifier });
          }
          onHideAnnotation({ annotationId: annotation.identifier });
        });
      }
    },
    [onCloseGroup, onHideAnnotation, getOrderedSelectedAnnotations, onHideGroup, indexedAnnotations, isMultiDrag],
  );

  const onDragMove = useCallback(
    (event: DragMoveEvent) => {
      const { over, active, delta } = event;

      // Skip initial frame with no movement — rects are stale while layout shifts
      if (delta.x === 0 && delta.y === 0) return;

      const sourceData = active.data.current as DragSourceData;
      const selectedIds = isMultiDrag() ? getOrderedSelectedAnnotations() : [{ type: sourceData.type, identifier: sourceData.identifier }];

      // Edge zone detection (above/below the list)
      const pointerY = currentPointerYRef.current;
      if (pointerY !== null) {
        const zone = resolvePointerZone(pointerY, listRef.current);
        if (zone) {
          if (zone !== lastPointerZoneRef.current) {
            lastPointerZoneRef.current = zone;
            const toIndex = zone === 'top' ? 0 : dndAnnotations.length;
            try {
              validateMove({
                destGroupId: undefined,
                selectedIdentifiers: selectedIds,
                annotations: indexedAnnotations,
              });
              setDropTarget({ toIndex, toGroupIdentifier: undefined });
            } catch {
              // Validation failed (e.g. room locked to floor group).
              // Keep the previous valid drop target to avoid flicker.
            }
          }
          return;
        }
        if (lastPointerZoneRef.current) {
          lastPointerZoneRef.current = undefined;
        }
      }

      // No collision target — clear indicator
      if (!over) {
        setDropTarget(null);
        return;
      }

      const destData = over.data.current as DestData;

      // Use pointer Y for both single and multi-drag. The dragged item returns
      // null (hidden), so the list collapses and `initialRect`-based calculations
      // would be offset by the missing card height — causing the indicator to
      // appear one position higher than where the item actually lands.
      if (pointerY === null) return;
      const cardCenterY = pointerY;

      try {
        const { toIndex, toGroupIdentifier } = calculateDropPosition(cardCenterY, destData, over.rect, selectedIds, dndAnnotations, openGroups);
        setDropTarget({ toIndex, toGroupIdentifier });
      } catch (e) {
        const message = (e as Error)?.message;
        setDropTarget(message ? { error: message } : null);
      }
    },
    [isMultiDrag, setDropTarget, getOrderedSelectedAnnotations, indexedAnnotations, dndAnnotations, openGroups],
  );

  // IDs of items being moved — kept hidden via React (not DOM) between the
  // dnd-kit drag end and the mutation response to prevent the old list flashing.
  const [pendingHideIds, setPendingHideIds] = useState<Set<string> | null>(null);

  // When the annotation list updates after a drop mutation, clear the drag-hide mask.
  // Done during render so the rows un-hide in the same commit that brings the new list,
  // rather than one extra render later (which showed as a flash).
  useOnValueChange(indexedAnnotations, () => {
    if (pendingHideIds) setPendingHideIds(null);
  });

  const unhideAllDragItems = useCallback(() => {
    closedGroupItems.current.forEach((el) => el.classList.remove('hidden'));
    closedGroupItems.current = [];
    hiddenAnnotationItems.current.forEach((el) => el.classList.remove('hidden'));
    hiddenAnnotationItems.current = [];
    document.querySelectorAll<HTMLElement>('.hidden[data-annotation-id], .hidden[data-group-id], .hidden[data-group-children]').forEach((el) => el.classList.remove('hidden'));
  }, []);

  const resetDragState = useCallback(() => {
    setActiveId(null);
    setDropTarget(null);
    lastPointerZoneRef.current = undefined;
    dragSourcePositionRef.current = null;
    dragListenersCleanupRef.current?.();
    dragListenersCleanupRef.current = null;
    unhideAllDragItems();
  }, [setDropTarget, unhideAllDragItems]);

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      // Read stored refs before cleanup clears them
      const target = dropTargetRef.current;
      const dragSourcePosition = dragSourcePositionRef.current;

      if (!target || 'error' in target) {
        resetDragState();

        if (target && 'error' in target) {
          switch (target.error) {
            case 'ROOM_MUST_STAY_IN_FLOOR':
              showToast({ title: actionNotSupportedTitle, text: roomsStayInFloorText, type: ToastType.WARNING });
              break;
            case 'SKELETON_MUST_STAY_IN_FLOOR':
              showToast({ title: actionNotSupportedTitle, text: wallSketchesStayInFloorText, type: ToastType.WARNING });
              break;
            case 'ROOM_FOLDER_MUST_STAY_IN_FLOOR':
              showToast({ title: actionNotSupportedTitle, text: roomFoldersStayInFloorText, type: ToastType.WARNING });
              break;
            case 'SKETCH_MUST_STAY_IN_FLOOR':
              showToast({ title: actionNotSupportedTitle, text: sketchesStayInFloorText, type: ToastType.WARNING });
              break;
            case 'ONLY_SKETCHES_IN_ROOM_FOLDER':
              showToast({ title: actionNotSupportedTitle, text: onlySketchesInRoomFolderText, type: ToastType.WARNING });
          }
        }
        return;
      }

      const { toIndex: visualIndex, toGroupIdentifier } = target;

      const { active } = event;
      const sourceData = active.data.current as DragSourceData;
      const { type, identifier } = sourceData;

      const identifiers: { type: string; identifier: string }[] = isMultiDrag(identifier) ? orderBy('index', 'asc', getOrderedSelectedAnnotations()) : [{ type, identifier }];

      const movedIds = new Set(identifiers.map((i) => i.identifier));
      // The drag ran on the VISIBLE tree — map its index into the full
      // ordering context (hidden rows must not shift where the drop lands).
      const fullVisualIndex = toFullContextIndex(visualIndex, toGroupIdentifier, dndAnnotations, indexedAnnotations);
      const toIndex = computeInsertionIndex(fullVisualIndex, toGroupIdentifier, movedIds, indexedAnnotations);

      if (!isMultiDrag(identifier) && dragSourcePosition && toGroupIdentifier === dragSourcePosition.groupId && toIndex === dragSourcePosition.index) {
        resetDragState();
        return;
      }

      // Hide moved items via React state so they stay invisible between the
      // dnd-kit drag end (which un-sets isDragging) and the mutation response.
      setPendingHideIds(movedIds);
      resetDragState();

      executePositionUpdate({
        toIndex,
        identifiers: identifiers.map(({ type, identifier }) => ({ type, identifier })),
        toGroupIdentifier,
      });
    },
    [
      getOrderedSelectedAnnotations,
      executePositionUpdate,
      showToast,
      resetDragState,
      isMultiDrag,
      indexedAnnotations,
      dndAnnotations,
      actionNotSupportedTitle,
      roomsStayInFloorText,
      wallSketchesStayInFloorText,
      roomFoldersStayInFloorText,
      sketchesStayInFloorText,
      onlySketchesInRoomFolderText,
    ],
  );

  const onDragCancel = useCallback(() => {
    resetDragState();
  }, [resetDragState]);

  // Use pointer-based collision for multi-drag (cursor position matters),
  // card-center-based collision for single drag
  const collisionDetection: CollisionDetection = useCallback(
    (args) => {
      if (isMultiDrag()) {
        return pointerWithin(args);
      }
      return closestCenter(args);
    },
    [isMultiDrag],
  );

  return (
    <>
      <ContextMenu
        className="flex-1 min-h-0"
        buttonClassName="h-full flex flex-col"
        buttonChild={
          <div className="flex flex-col grow w-full h-full mt-8 space-y-1 overflow-y-auto" onClick={onClickContainer}>
            <DndContext sensors={sensors} collisionDetection={collisionDetection} onDragStart={onDragStart} onDragMove={onDragMove} onDragEnd={onDragEnd} onDragCancel={onDragCancel}>
              <SortableContext items={sortableItems} strategy={verticalListSortingStrategy}>
                <div ref={scrollContainerRef} className="h-full overflow-y-auto">
                  <div ref={listRef} className="flow-root">
                    <AnnotationListItems
                      ids={visibleAnnotationIds}
                      project={project}
                      isReadOnly={isReadOnly}
                      onClickAnnotation={onClickAnnotation}
                      activeId={activeId}
                      dropIndicator={dropIndicator}
                      dragOverGroupId={dragOverGroupId}
                      openGroups={openGroups}
                      onGroupOpenChange={onGroupOpenChange}
                      pendingHideIds={pendingHideIds}
                      openingsByRoomId={openingsByRoomId}
                      openRooms={openRooms}
                      onRoomOpenChange={onRoomOpenChange}
                    />
                  </div>
                </div>
              </SortableContext>
              <DragOverlay modifiers={[snapYCenterToCursor]} dropAnimation={null}>
                {activeId && getAnnotationsByIdentifier()[activeId] ? (
                  <DragOverlayContentWrapper item={getAnnotationsByIdentifier()[activeId]} isMultiDrag={isMultiDrag} annotationsByIdentifier={getAnnotationsByIdentifier()} />
                ) : activeId ? (
                  <div className="px-2 py-2 text-sm">{draggingText}</div>
                ) : null}
              </DragOverlay>
            </DndContext>
          </div>
        }
      >
        {!isReadOnly && (
          <SelectItemContent icon={FolderIcon} onClick={openCreateGroupModal}>
            <T _str="create group" swc />
          </SelectItemContent>
        )}
      </ContextMenu>
      <AnnotationGroupModal onClose={closeCreateGroupModal} open={createGroupModalOpen} />
    </>
  );
};

export const Annotations = memo(Annotations_);
